import React, { useState, useEffect } from 'react';
import { ConsolidacaoPlacaPreview, ConsolidacaoResultado } from '../../types/veiculoConsolidacao';
import { veiculoConsolidacaoService } from '../../services/veiculoConsolidacaoService';
import { 
  Merge, 
  ShieldCheck, 
  CheckCircle2, 
  AlertTriangle, 
  Truck, 
  User, 
  History, 
  FileText, 
  ArrowRight, 
  X, 
  RefreshCw, 
  Check, 
  AlertCircle
} from 'lucide-react';

import { VeiculoMarca, VeiculoModelo, VeiculoCor, VeiculoAno } from '../../types/veiculo';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculos: any[];
  motoristasList: { id: number; nome?: string; name?: string }[];
  marcasList: (VeiculoMarca | { id: number; nome?: string; marca?: string | null })[];
  modelosList: (VeiculoModelo | { id: number; nome?: string; modelo?: string | null })[];
  anosList: (VeiculoAno | { id: number; ano: number })[];
  coresList: (VeiculoCor | { id: number; nome?: string; cor?: string })[];
  onSuccess: () => void;
}

export const VeiculoConsolidacaoModal: React.FC<Props> = ({
  isOpen,
  onClose,
  veiculos,
  motoristasList,
  marcasList,
  modelosList,
  anosList,
  coresList,
  onSuccess,
}) => {
  const [previews, setPreviews] = useState<ConsolidacaoPlacaPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [placaProcessando, setPlacaProcessando] = useState<string | null>(null);
  const [resultados, setResultados] = useState<ConsolidacaoResultado[]>([]);
  const [etapa, setEtapa] = useState<'preview' | 'concluido'>('preview');

  useEffect(() => {
    if (isOpen) {
      carregarPreviews();
      setResultados([]);
      setEtapa('preview');
    }
  }, [isOpen, veiculos]);

  const carregarPreviews = async () => {
    try {
      setLoading(true);
      const pessoasMap = new Map<number, string>(motoristasList.map(m => [m.id, m.nome || (m as any).name || '']));
      const marcasMap = new Map<number, string>(marcasList.map(m => [m.id, (m as any).nome || (m as any).marca || '']));
      const modelosMap = new Map<number, string>(modelosList.map(m => [m.id, (m as any).nome || (m as any).modelo || '']));
      const anosMap = new Map<number, string>(anosList.map(a => [a.id, String(a.ano)]));
      const coresMap = new Map<number, string>(coresList.map(c => [c.id, (c as any).nome || (c as any).cor || '']));

      const lista = await veiculoConsolidacaoService.gerarPreviewConsolidacao(
        veiculos,
        pessoasMap,
        marcasMap,
        modelosMap,
        anosMap,
        coresMap
      );
      setPreviews(lista);
    } catch (err) {
      console.error('Erro ao gerar preview de consolidação:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConsolidarUma = async (preview: ConsolidacaoPlacaPreview) => {
    try {
      setPlacaProcessando(preview.placa);
      const res = await veiculoConsolidacaoService.consolidarPlaca(preview);
      setResultados(prev => [...prev.filter(r => r.placa !== res.placa), res]);
      
      // Remove da lista de previews
      setPreviews(prev => prev.filter(p => p.placa !== preview.placa));
      onSuccess();
    } catch (err) {
      console.error(`Erro ao consolidar placa ${preview.placa}:`, err);
    } finally {
      setPlacaProcessando(null);
    }
  };

  const handleConsolidarTodas = async () => {
    if (previews.length === 0) return;
    try {
      setProcessando(true);
      const resList = await veiculoConsolidacaoService.consolidarTodas(previews);
      setResultados(resList);
      setPreviews([]);
      setEtapa('concluido');
      onSuccess();
    } catch (err) {
      console.error('Erro ao consolidar todas:', err);
    } finally {
      setProcessando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-between border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-600 rounded-xl shadow-inner">
              <Merge className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold flex items-center gap-2">
                Unificar Veículos com Placas Duplicadas
              </h3>
              <p className="text-xs text-slate-300">
                Consolide em um único cadastro oficial por placa e converta as duplicatas em histórico de condutores
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Security / Guarantee Banner */}
        <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-3 flex items-center gap-3 text-emerald-950 text-xs">
          <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
          <div>
            <strong className="font-bold text-emerald-900">Segurança Total de Dados:</strong> Nenhuma Ordem de Serviço será perdida ou terá seu motorista alterado. Todas as viagens feitas nos cadastros antigos serão associadas com precisão ao respectivo motorista no histórico do veículo principal.
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 bg-slate-50/50">
          
          {loading ? (
            <div className="py-16 text-center text-slate-400 text-xs flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-red-600" />
              <span>Analisando placas duplicadas e mapeando viagens passadas...</span>
            </div>
          ) : etapa === 'concluido' || (previews.length === 0 && resultados.length > 0) ? (
            <div className="space-y-4">
              <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-200 text-center space-y-2">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto text-white shadow-md">
                  <Check className="w-6 h-6" />
                </div>
                <h4 className="text-base font-bold text-emerald-950">
                  Unificação Concluída com Sucesso!
                </h4>
                <p className="text-xs text-emerald-800 max-w-md mx-auto">
                  Todas as placas duplicadas foram unificadas. O cadastro de veículos agora está limpo e o histórico de cada motorista está salvo na linha do tempo de cada carro.
                </p>
              </div>

              {/* Tabela de Resultados */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-2xs">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-3xs border-b border-slate-200">
                    <tr>
                      <th className="p-3">Placa</th>
                      <th className="p-3">Cadastros Unificados</th>
                      <th className="p-3">OSs Migradas e Preservadas</th>
                      <th className="p-3">Histórico de Motoristas</th>
                      <th className="p-3 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {resultados.map((res) => (
                      <tr key={res.placa} className="hover:bg-slate-50">
                        <td className="p-3 font-mono font-black text-slate-900">{res.placa}</td>
                        <td className="p-3 text-slate-600">{res.removidosCount + 1} cadastros → 1 cadastro oficial</td>
                        <td className="p-3 font-semibold text-slate-800">{res.osMigradasCount} viagens mantidas</td>
                        <td className="p-3 text-slate-600">{res.historicoRegistradoCount} condutores registrados</td>
                        <td className="p-3 text-right">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-full font-bold text-3xs">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Consolidado
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : previews.length === 0 ? (
            <div className="py-12 px-6 text-center bg-white rounded-2xl border border-dashed border-slate-300 space-y-3">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
              <h4 className="text-sm font-bold text-slate-800">
                Nenhuma placa duplicada encontrada!
              </h4>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                Sua frota já está 100% unificada com 1 cadastro por veículo físico e o histórico de motoristas ativo.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              
              {/* Batch Action Bar */}
              <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-2xs flex-wrap gap-3">
                <div>
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                    Encontramos {previews.length} {previews.length === 1 ? 'placa' : 'placas'} com múltiplos cadastros
                  </h4>
                  <p className="text-xs text-slate-500">
                    Você pode unificar uma por uma ou clicar no botão ao lado para consolidar todas de uma vez.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleConsolidarTodas}
                  disabled={processando || placaProcessando !== null}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl text-xs font-bold hover:bg-red-700 transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                >
                  {processando ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" /> Unificando Todas...
                    </>
                  ) : (
                    <>
                      <Merge className="w-4 h-4" /> Unificar Todas as {previews.length} Placas
                    </>
                  )}
                </button>
              </div>

              {/* Cards List for each duplicate plate */}
              <div className="space-y-4">
                {previews.map((preview) => {
                  const isBusy = placaProcessando === preview.placa || processando;
                  return (
                    <div 
                      key={preview.placa}
                      className="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-4 transition-all hover:border-slate-300"
                    >
                      {/* Card Top: Placa & Action */}
                      <div className="flex items-start justify-between gap-4 flex-wrap pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          <span className="px-3 py-1.5 bg-slate-900 text-amber-400 font-mono text-sm font-black rounded-lg border border-slate-800 shadow-xs">
                            {preview.placa}
                          </span>
                          <div>
                            <span className="text-xs font-bold text-slate-900">
                              {preview.totalCadastros} cadastros no banco
                            </span>
                            <span className="mx-2 text-slate-300">•</span>
                            <span className="text-xs text-slate-500">
                              {preview.totalOSVinculadas} viagens vinculadas
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleConsolidarUma(preview)}
                          disabled={isBusy}
                          className="px-3 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition-colors shadow-2xs flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                        >
                          {placaProcessando === preview.placa ? (
                            <>
                              <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-400" /> Processando...
                            </>
                          ) : (
                            <>
                              <Merge className="w-3.5 h-3.5 text-amber-400" /> Unificar Placa {preview.placa}
                            </>
                          )}
                        </button>
                      </div>

                      {/* Card Body: Principal vs Secundarios */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                        
                        {/* Principal (Ficará ativo) */}
                        <div className="p-3.5 bg-emerald-50/60 rounded-xl border border-emerald-200 space-y-2">
                          <span className="text-3xs font-black uppercase text-emerald-800 tracking-wider flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            Cadastro Oficial que será mantido
                          </span>
                          <div className="space-y-1 text-slate-700">
                            <div className="font-bold text-slate-900 flex items-center gap-1">
                              <Truck className="w-3.5 h-3.5 text-slate-500" />
                              {[preview.veiculoPrincipal.marca_nome, preview.veiculoPrincipal.modelo_nome, preview.veiculoPrincipal.ano_nome].filter(Boolean).join(' ') || 'Veículo Padrão'}
                            </div>
                            <div className="text-slate-600 flex items-center gap-1">
                              <User className="w-3.5 h-3.5 text-slate-400" />
                              Motorista Atual: <strong>{preview.veiculoPrincipal.motorista_nome}</strong>
                            </div>
                            {preview.veiculoPrincipal.renavam && (
                              <div className="text-3xs text-slate-500">Renavam: {preview.veiculoPrincipal.renavam}</div>
                            )}
                          </div>
                        </div>

                        {/* Motoristas que entrarão no Histórico */}
                        <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                          <span className="text-3xs font-black uppercase text-slate-600 tracking-wider flex items-center gap-1">
                            <History className="w-3.5 h-3.5 text-red-600" />
                            Condutores preservados no Histórico ({preview.motoristasEnvolvidos.length})
                          </span>
                          <div className="space-y-1.5 max-h-28 overflow-y-auto pr-1">
                            {preview.motoristasEnvolvidos.map((mot, idx) => (
                              <div key={idx} className="flex items-center justify-between text-3xs bg-white p-1.5 rounded-lg border border-slate-200">
                                <span className="font-semibold text-slate-800 truncate">{mot.motorista_nome}</span>
                                <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-medium shrink-0">
                                  {mot.totalOS} {mot.totalOS === 1 ? 'OS' : 'OSs'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>

                    </div>
                  );
                })}
              </div>

            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {previews.length > 0 ? `${previews.length} placas aguardando unificação` : 'Tudo pronto'}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-bold hover:bg-slate-700 transition-colors shadow-2xs"
            >
              Fechar
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
